import { Breadcrumbs, Loader, Tabs } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { useDisclosure } from '@mantine/hooks';
import {
    IconClock,
    IconKey,
    IconLayoutDashboard,
    IconPackages,
    IconRefresh,
    IconWebhook,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import ActivationGuard from '@xver/components/Atoms/ActivationGuard';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

import { Button } from '../../../../components/Atoms';
import useBreadcrumbItems from '../../../../hooks/useBreadcrumbItems';
import usePageHeading from '../../../../hooks/usePageHeading';
import AuthenticationModal from './components/AuthenticationModal';
import NewPasswordRevealModal from './components/NewPasswordRevealModal';
import OverviewTabPanel from './components/OverviewTabPanel';
import RepositoriesTabPanel from './components/RepositoriesTabPanel';
import ResetPasswordConfirmModal from './components/ResetPasswordConfirmModal';
import RetentionTabPanel from './components/RetentionTabPanel';
import WebhooksTabPanel from './components/WebhooksTabPanel';
import { PROJECT_QUERY_KEY } from './hooks/useProjectQuery';
import { useProjectsListQuery } from './hooks/useProjectsListQuery';
import { REPOSITORIES_QUERY_KEY } from './hooks/useRepositoriesQuery';
import { RETENTION_POLICIES_QUERY_KEY } from './hooks/useRetentionPoliciesQuery';
import { WEBHOOKS_QUERY_KEY } from './hooks/useWebhooksQuery';

type TabValue = 'overview' | 'repositories' | 'webhooks' | 'retention';

const VALID_TABS: ReadonlySet<TabValue> = new Set([
    'overview',
    'repositories',
    'webhooks',
    'retention',
]);

function parseTab(value: string | null): TabValue {
    if (value && (VALID_TABS as ReadonlySet<string>).has(value)) {
        return value as TabValue;
    }
    return 'overview';
}

export default function ArtifactRegistryPage() {
    const { t } = useTranslation('artifactRegistry');
    const headingRef = usePageHeading(t('page.title', 'Artifact Registry'));
    const queryClient = useQueryClient();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const [authModalOpened, { open: openAuthModal, close: closeAuthModal }] = useDisclosure(false);
    const [confirmModalOpened, { open: openConfirmModal, close: closeConfirmModal }] =
        useDisclosure(false);
    const [revealModalOpened, { open: openRevealModal, close: closeRevealModal }] =
        useDisclosure(false);
    const [newPassword, setNewPassword] = useState('');

    const handleResetRequested = () => {
        closeAuthModal();
        openConfirmModal();
    };

    const handleResetSuccess = (pwd: string) => {
        setNewPassword(pwd);
        openRevealModal();
    };

    const handleRevealClose = () => {
        closeRevealModal();
        setNewPassword('');
    };

    const {
        data: projectsData,
        isPending: isProjectsPending,
        refetch: refetchProjects,
    } = useProjectsListQuery();

    const projects = useMemo(() => projectsData?.projects ?? [], [projectsData]);

    const projectParam = searchParams.get('project');
    const firstProjectName = projects[0]?.name ?? '';
    const selectedProject = useMemo(() => {
        if (projectParam && projects.some((p) => p.name === projectParam)) {
            return projectParam;
        }
        return firstProjectName;
    }, [projectParam, projects, firstProjectName]);

    useEffect(() => {
        if (!selectedProject) return;
        if (projectParam === selectedProject) return;
        const params = new URLSearchParams(searchParams);
        params.set('project', selectedProject);
        setSearchParams(params, { replace: true });
    }, [selectedProject, projectParam, searchParams, setSearchParams]);

    const activeTab = parseTab(searchParams.get('tab'));

    const handleProjectChange = (value: string | null) => {
        if (!value) return;
        const params = new URLSearchParams(searchParams);
        params.set('project', value);
        setSearchParams(params, { replace: true });
    };

    const handleTabChange = (next: string | null) => {
        if (!next) return;
        const params = new URLSearchParams(searchParams);
        if (next === 'overview') {
            params.delete('tab');
        } else {
            params.set('tab', next);
        }
        setSearchParams(params, { replace: true });
    };

    const breadcrumbItems = useBreadcrumbItems([
        { title: t('breadcrumb.product', 'Products'), href: '#' },
        {
            title: t('breadcrumb.artifactRegistry', 'Artifact Registry'),
            href: '/artifact-registry',
        },
    ]);

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await refetchProjects();
            if (selectedProject) {
                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: [PROJECT_QUERY_KEY, selectedProject],
                    }),
                    queryClient.invalidateQueries({
                        queryKey: [REPOSITORIES_QUERY_KEY, selectedProject],
                    }),
                    queryClient.invalidateQueries({
                        queryKey: [WEBHOOKS_QUERY_KEY, selectedProject],
                    }),
                    queryClient.invalidateQueries({
                        queryKey: [RETENTION_POLICIES_QUERY_KEY, selectedProject],
                    }),
                ]);
            }
        } finally {
            setIsRefreshing(false);
        }
    };

    const projectOptions = useMemo(
        () => projects.map((p) => ({ value: p.name, label: p.name })),
        [projects]
    );

    return (
        <>
            <div className="w-full min-h-full flex flex-col justify-start items-center gap-8">
                <div className="w-full flex flex-col justify-start items-start">
                    <Breadcrumbs separatorMargin="xs" className="mb-6">
                        {breadcrumbItems}
                    </Breadcrumbs>
                    <div className="w-full flex flex-col gap-2 justify-between lg:flex-row lg:gap-0">
                        <div className="flex flex-col justify-start items-start gap-1">
                            <h1 ref={headingRef} tabIndex={-1} className="text-3xl font-bold">
                                {t('page.title', 'Artifact Registry')}
                            </h1>
                            <p className="w-auto max-w-135 text-base text-fg/68">
                                {t(
                                    'page.description',
                                    'Store, version, and distribute container images for your Container Engine workloads.'
                                )}
                            </p>
                        </div>
                        <div className="flex flex-row justify-start items-end gap-2 lg:justify-end">
                            <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<IconKey stroke={1.5} />}
                                onClick={openAuthModal}
                            >
                                {t('buttons.authentication', 'Authentication')}
                            </Button>
                            <Button
                                size="sm"
                                variant="secondary"
                                leftIcon={<IconRefresh stroke={1.5} />}
                                onClick={() => {
                                    void handleRefresh();
                                }}
                                isLoading={isRefreshing}
                            >
                                {t('buttons.refresh', 'Refresh')}
                            </Button>
                        </div>
                    </div>
                </div>

                <div className="w-full">
                    <ActivationGuard personal>
                        {isProjectsPending ? (
                            <div className="w-full flex justify-center py-12">
                                <Loader size={24} color="var(--fg)" />
                            </div>
                        ) : projects.length === 0 ? (
                            <div className="w-full flex flex-col items-center justify-center gap-2 py-12 border-[0.5px] border-line rounded-md bg-surface-1">
                                <IconPackages size={36} stroke={1.25} className="text-fg/40" />
                                <p className="text-sm text-fg/68">
                                    {t(
                                        'projects.empty',
                                        'No Harbor projects found for this account.'
                                    )}
                                </p>
                            </div>
                        ) : (
                            <div className="w-full flex flex-col gap-6">
                                {projects.length > 1 && (
                                    <div className="w-full flex flex-row items-center gap-3">
                                        <span className="text-sm text-fg/68">
                                            {t('projects.label', 'Project')}
                                        </span>
                                        <Select
                                            value={selectedProject}
                                            onChange={handleProjectChange}
                                            data={projectOptions}
                                            searchable={projectOptions.length > 5}
                                            allowDeselect={false}
                                            size="sm"
                                            styles={{
                                                input: {
                                                    backgroundColor: 'var(--surface-2)',
                                                    border: '0.5px solid var(--line)',
                                                },
                                            }}
                                        />
                                    </div>
                                )}

                                <Tabs
                                    className="w-full space-y-4"
                                    value={activeTab}
                                    onChange={handleTabChange}
                                    color="var(--fg)"
                                    keepMounted={false}
                                >
                                    <Tabs.List aria-label={t('tabs.label')}>
                                        <Tabs.Tab
                                            value="overview"
                                            leftSection={<IconLayoutDashboard size={20} />}
                                        >
                                            {t('tabs.overview', 'Overview')}
                                        </Tabs.Tab>
                                        <Tabs.Tab
                                            value="repositories"
                                            leftSection={<IconPackages size={20} />}
                                        >
                                            {t('tabs.repositories', 'Repositories')}
                                        </Tabs.Tab>
                                        <Tabs.Tab
                                            value="webhooks"
                                            leftSection={<IconWebhook size={20} />}
                                        >
                                            {t('tabs.webhooks', 'Webhooks')}
                                        </Tabs.Tab>
                                        <Tabs.Tab
                                            value="retention"
                                            leftSection={<IconClock size={20} />}
                                        >
                                            {t('tabs.retention', 'Retention')}
                                        </Tabs.Tab>
                                    </Tabs.List>

                                    <Tabs.Panel value="overview" className="w-full">
                                        <OverviewTabPanel projectIdOrName={selectedProject} />
                                    </Tabs.Panel>
                                    <Tabs.Panel value="repositories" className="w-full">
                                        <RepositoriesTabPanel projectIdOrName={selectedProject} />
                                    </Tabs.Panel>
                                    <Tabs.Panel value="webhooks" className="w-full">
                                        <WebhooksTabPanel projectIdOrName={selectedProject} />
                                    </Tabs.Panel>
                                    <Tabs.Panel value="retention" className="w-full">
                                        <RetentionTabPanel projectIdOrName={selectedProject} />
                                    </Tabs.Panel>
                                </Tabs>
                            </div>
                        )}
                    </ActivationGuard>
                </div>
            </div>

            <AuthenticationModal
                opened={authModalOpened}
                onClose={closeAuthModal}
                onResetRequested={handleResetRequested}
            />
            <ResetPasswordConfirmModal
                opened={confirmModalOpened}
                onClose={closeConfirmModal}
                onSuccess={handleResetSuccess}
            />
            <NewPasswordRevealModal
                opened={revealModalOpened}
                newPassword={newPassword}
                onClose={handleRevealClose}
            />
        </>
    );
}
